import { ProductPriceModeTagEnum } from '../models/product-price-mode-tag.enum';
import { EasyPricingState } from '@usf/ngrx-pricing';
import { TagProduct } from '@app/shared-product/components/usf-product-tag-list/models/tag-product.model';
import { ChipTypeEnum } from '@app/shared-product/components/usf-chip/models/chip-type.enum';
import { Product } from '@shared/models/product.model';

/**
 * Configuration for price mode tags with their display properties
 */
const PRICE_MODE_TAG_CONFIG: Record<
  ProductPriceModeTagEnum,
  Omit<TagProduct, 'content'> & { label: string }
> = {
  [ProductPriceModeTagEnum.PriceAtOrAboveTarget]: {
    label: 'Product Priced at Target or Above',
    type: ChipTypeEnum.image,
    iconUrl: 'assets/icon/pme-green-checkmark.svg',
    clickable: false,
    iconConfig: {
      iconWidth: 1.5,
      iconHeight: 1.5,
    },
  },
  [ProductPriceModeTagEnum.LocallyManagedPrice]: {
    label: 'Price is locally managed',
    type: ChipTypeEnum.image,
    iconUrl: 'assets/icon/pme-red-l.svg',
    clickable: false,
    iconConfig: {
      iconWidth: 1.5,
      iconHeight: 1.5,
    },
  },
  [ProductPriceModeTagEnum.PassengerItem]: {
    label: 'Passenger Item',
    type: ChipTypeEnum.image,
    iconUrl: 'assets/icon/pme-passenger.svg',
    clickable: false,
    iconConfig: {
      iconWidth: 1.5,
      iconHeight: 1.5,
    },
  },
  [ProductPriceModeTagEnum.CprAutoAssist]: {
    label: 'CPR Auto Assist',
    type: ChipTypeEnum.image,
    iconUrl: 'assets/icon/pme-cpr.svg',
    clickable: false,
    iconConfig: {
      iconWidth: 1.5,
      iconHeight: 1.5,
    },
  },
  [ProductPriceModeTagEnum.Breakable]: {
    label: 'Breakable',
    type: ChipTypeEnum.image,
    iconUrl: 'assets/icon/pme-scissors.svg',
    clickable: false,
    iconConfig: {
      iconWidth: 1.5,
      iconHeight: 1.5,
    },
  },
  [ProductPriceModeTagEnum.Contract]: {
    label: 'Contract',
    type: ChipTypeEnum.image,
    iconUrl: 'assets/icon/pme-contract.svg',
    clickable: false,
    iconConfig: {
      iconWidth: 1.5,
      iconHeight: 1.5,
    },
  },
};

/**
 * Determines which price mode tags should be displayed for a product
 */
function determinePriceModeTagEnums(
  product: Product,
): ProductPriceModeTagEnum[] {
  if (!product) return [];

  const tags: ProductPriceModeTagEnum[] = [];
  const easyPricingState = product.easyPricingViewModel as
    | EasyPricingState
    | undefined;

  // Pricing-related tags
  if (easyPricingState?.currentPrice) {
    const currentPrice = easyPricingState.currentPrice;
    const targetPrice = currentPrice.cookbookPrices?.cookbookTargetPrice;
    const unitPrice = currentPrice.unitPrice;
    const ascFlag = currentPrice.autoShippingCharges?.ascApplyFlag;
    const unitPriceBeforeAsc =
      currentPrice.autoShippingCharges?.unitPriceBeforeAsc;

    // Price at or above target
    if (targetPrice != null) {
      const isPriceAtTarget =
        (ascFlag === 'P' &&
          unitPriceBeforeAsc != null &&
          unitPriceBeforeAsc >= targetPrice) ||
        ((ascFlag === 'N' || ascFlag === 'S') &&
          unitPrice != null &&
          unitPrice >= targetPrice);

      if (isPriceAtTarget) {
        tags.push(ProductPriceModeTagEnum.PriceAtOrAboveTarget);
      }
    }

    // Locally managed price
    if (easyPricingState.zilliantDetails?.priceOptimizedInd === 'N') {
      tags.push(ProductPriceModeTagEnum.LocallyManagedPrice);
    }

    // Passenger item
    if (currentPrice.priceControls?.passengerCprInd === 'Y') {
      tags.push(ProductPriceModeTagEnum.PassengerItem);
    }

    // CPR auto assist
    if (easyPricingState.zilliantDetails?.cprAutoAssist === 'Y') {
      tags.push(ProductPriceModeTagEnum.CprAutoAssist);
    }

    // Contract
    if (
      currentPrice.priceMethod === 3 &&
      currentPrice.priceControls?.cprSetupInd === 'N'
    ) {
      tags.push(ProductPriceModeTagEnum.Contract);
    }
  }

  // Breakable (from product summary)
  if (product.summary?.breakable === true) {
    tags.push(ProductPriceModeTagEnum.Breakable);
  }

  return tags;
}

/**
 * Creates TagProduct objects from price mode tag enums
 */
function createTagProducts(tagEnums: ProductPriceModeTagEnum[]): TagProduct[] {
  return tagEnums.map(tagEnum => {
    const config = PRICE_MODE_TAG_CONFIG[tagEnum];
    return {
      content: config.label,
      type: config.type,
      iconUrl: config.iconUrl,
      clickable: config.clickable,
      iconConfig: config.iconConfig,
    };
  });
}

/**
 * Gets all price mode tags for a product as TagProduct array
 */
export function getPriceModeTags(product: Product): TagProduct[] {
  const tagEnums = determinePriceModeTagEnums(product);
  return createTagProducts(tagEnums);
}
